import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Briefcase, ClipboardCheck, HandHeart, AlarmClock, CalendarClock,
    CheckCircle2, ArrowRight, RefreshCw, Loader2, Filter,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    formatDate, formatUserName, StateBadge, PriorityBadge,
    PageLoading, FriendlyDeniedError,
} from "./components/ui";


const API_PATH = "/projects";


const CATEGORIES = [
    { key: "assigned_to_me",       label: "Assigned to Me",     Icon: Briefcase,      tone: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200",   accent: "border-l-indigo-500" },
    { key: "awaiting_my_review",   label: "Awaiting My Review", Icon: ClipboardCheck, tone: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",      accent: "border-l-amber-500" },
    { key: "delegated_to_me",      label: "Delegated to Me",    Icon: HandHeart,      tone: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",            accent: "border-l-sky-500" },
    { key: "overdue",              label: "Overdue",            Icon: AlarmClock,     tone: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",         accent: "border-l-rose-500" },
    { key: "due_today",            label: "Due Today",          Icon: CalendarClock,  tone: "bg-orange-50 text-orange-700 ring-1 ring-orange-200",   accent: "border-l-orange-500" },
    { key: "recently_completed",   label: "Recently Completed", Icon: CheckCircle2,   tone: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", accent: "border-l-emerald-500" },
];


function WorkItemCard({ item }) {
    const to = `/projects/${item.engagement_id}/w/${item.id}`;
    return (
        <Link
            to={to}
            className="group block bg-white rounded-lg ring-1 ring-slate-200 hover:ring-indigo-300 hover:shadow-md transition p-4"
            data-testid={`mywork-card-${item.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-[11px] text-slate-500" data-testid={`mywork-wi-number-${item.id}`}>
                            {item.work_item_number}
                        </span>
                        <StateBadge state={item.state} testId={`mywork-state-${item.id}`} />
                        {item.priority && (
                            <PriorityBadge priority={item.priority} testId={`mywork-priority-${item.id}`} />
                        )}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-indigo-700" data-testid={`mywork-title-${item.id}`}>
                        {item.title}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-600" data-testid={`mywork-engagement-${item.id}`}>
                            {item.engagement_name || item.engagement_id}
                        </span>
                        {item.engagement_number && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="font-mono text-[10px] text-slate-400">{item.engagement_number}</span>
                            </>
                        )}
                        {item.role_label && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span>{item.role_label}</span>
                            </>
                        )}
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                        {item.target_date && (
                            <span data-testid={`mywork-due-${item.id}`}>
                                Due {formatDate(item.target_date)}
                            </span>
                        )}
                        {item.actual_completion_date && (
                            <span className="text-emerald-600" data-testid={`mywork-completed-${item.id}`}>
                                Completed {formatDate(item.actual_completion_date)}
                            </span>
                        )}
                        {item.primary_owner_snapshot && (
                            <span data-testid={`mywork-owner-${item.id}`}>
                                Owner: {formatUserName(item.primary_owner_snapshot, item.primary_owner_user_id)}
                            </span>
                        )}
                    </div>
                </div>
                <ArrowRight className="w-4 h-4 flex-shrink-0 text-slate-300 group-hover:text-indigo-600 mt-1" />
            </div>
        </Link>
    );
}


function CategorySection({ cat, bucket, query }) {
    const filtered = useMemo(() => {
        if (!query) return bucket.items;
        const q = query.toLowerCase();
        return bucket.items.filter((it) => (
            (it.title || "").toLowerCase().includes(q) ||
            (it.engagement_name || "").toLowerCase().includes(q) ||
            (it.work_item_number || "").toLowerCase().includes(q)
        ));
    }, [bucket.items, query]);

    const { label, Icon, tone, accent } = cat;
    return (
        <section
            className={`bg-white rounded-lg ring-1 ring-slate-200 border-l-4 ${accent} overflow-hidden`}
            data-testid={`mywork-section-${cat.key}`}
        >
            <header className={`flex items-center justify-between px-4 py-3 ${tone}`}>
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <h2 className="text-sm font-semibold">{label}</h2>
                    <span className="text-xs font-mono opacity-70" data-testid={`mywork-count-${cat.key}`}>
                        {filtered.length}{query && filtered.length !== bucket.total ? ` / ${bucket.total}` : ""}
                    </span>
                </div>
            </header>
            <div className="p-4 space-y-3">
                {filtered.length === 0 ? (
                    <div className="text-sm text-slate-400 italic" data-testid={`mywork-empty-${cat.key}`}>
                        {query ? "No matches for your filter." : "Nothing here right now."}
                    </div>
                ) : (
                    filtered.map((item) => (
                        <WorkItemCard key={`${cat.key}-${item.id}`} item={item} />
                    ))
                )}
            </div>
        </section>
    );
}


export default function MyWork() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorDetail, setErrorDetail] = useState(null);
    const [query, setQuery] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setErrorDetail(null);
        try {
            const r = await api.get(`${API_PATH}/my-work`);
            setData(r.data);
        } catch (err) {
            const raw = err?.response?.data?.detail;
            const msg = formatApiError(err);
            setError(msg);
            if (raw && typeof raw === "object") setErrorDetail(raw);
            toast.error(`Could not load My Work: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totalActive = useMemo(() => {
        if (!data) return 0;
        return (
            data.categories.assigned_to_me.total +
            data.categories.awaiting_my_review.total +
            data.categories.delegated_to_me.total
        );
    }, [data]);

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="mywork-page">
            <PageHeader
                eyebrow="Delivery"
                title="My Work"
                description="What you need to work on right now, across every project."
                data-testid="mywork-header"
            />

            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <div className="relative flex-1">
                    <Filter className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter by title, engagement, or work item number…"
                        className="pl-8"
                        data-testid="mywork-filter-input"
                    />
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="mywork-refresh-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Refresh
                </Button>
            </div>

            {/* User-facing summary — one clean sentence, no engineering fields. */}
            {data && (
                <div className="text-sm text-slate-600 flex items-center gap-3 flex-wrap" data-testid="mywork-summary">
                    <span>
                        <strong className="text-slate-900">{totalActive}</strong> active {totalActive === 1 ? "item" : "items"}
                        {data.categories.overdue.total > 0 && (
                            <> · <strong className="text-rose-600">{data.categories.overdue.total}</strong> overdue</>
                        )}
                        {data.categories.due_today.total > 0 && (
                            <> · <strong className="text-orange-600">{data.categories.due_today.total}</strong> due today</>
                        )}
                    </span>
                </div>
            )}

            {/* Engineering diagnostics — Super Admin ONLY. Regular users
                do not see scanned / authorized_hidden / engagements_fetched
                / hard_cap technical fields (per REEDOS Iter-7 direction). */}
            {data && isSuperAdmin && (
                <details
                    className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-md px-3 py-2"
                    data-testid="mywork-admin-diagnostics"
                >
                    <summary className="cursor-pointer text-slate-600">Admin diagnostics</summary>
                    <div className="mt-2 flex flex-wrap gap-3">
                        <span>{data.performance.work_items_scanned} scanned</span>
                        <span>{data.performance.authorized_shown} authorised</span>
                        <span data-testid="mywork-hidden-count">
                            {data.performance.authorized_hidden} hidden (no access)
                        </span>
                        <span>{data.performance.engagements_fetched} engagements fetched</span>
                        {data.performance.hard_cap_hit && (
                            <span className="text-amber-700">Hard cap reached — narrow your queue</span>
                        )}
                    </div>
                </details>
            )}

            {error && errorDetail && (
                <FriendlyDeniedError detail={errorDetail} />
            )}
            {error && !errorDetail && (
                <div className="text-sm text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-md p-3" data-testid="mywork-error">
                    {error}
                </div>
            )}

            {loading && !data && (
                <PageLoading label="Loading your work…" testId="mywork-loading" />
            )}

            {data && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mywork-sections-grid">
                    {CATEGORIES.map((cat) => (
                        <CategorySection
                            key={cat.key}
                            cat={cat}
                            bucket={data.categories[cat.key]}
                            query={query}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
